import { useCallback, useEffect, useState } from 'react';
import { PnlReportListItem } from '../interfaces/pnl-report.interfaces';
import { fetchPnlReportList } from '../services/pnl-report-service';

interface UsePnlReportListResult {
  readonly reports: readonly PnlReportListItem[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly reload: () => void;
}

/**
 * Fetches the list of saved P&L reports with their status.
 */
export const usePnlReportList = (): UsePnlReportListResult => {
  const [reports, setReports] = useState<readonly PnlReportListItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const reload = useCallback((): void => {
    setReloadToken((t) => t + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const load = async (): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchPnlReportList(controller.signal);
        if (!controller.signal.aborted) {
          // TEMP: strip the "AmazonFederate_" prefix from created_by until the BE
          // returns a clean alias. Remove this map once the BE fix lands.
          setReports(
            response.reports.map((report) => ({
              ...report,
              created_by: report.created_by?.replace('AmazonFederate_', '') ?? report.created_by
            }))
          );
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load reports');
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => controller.abort();
  }, [reloadToken]);

  return { reports, isLoading, error, reload };
};
